// The signed-in person of the page: resolved once from the provider's session (./session.ts), kept while
// the page lives. A route that needs a person sends everyone else to the app's own sign-in page.
import * as React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { fetchAppToken, type AppCaller, type TokenAnswer } from './session.ts';

export type AppAuthState =
  | { status: 'loading' }
  | { status: 'signed-in'; caller: AppCaller }
  | { status: 'signed-out'; refused?: string; error?: string };

const Ctx = React.createContext<{ state: AppAuthState; refresh: () => Promise<void> } | null>(null);

const stateOf = (a: TokenAnswer): AppAuthState =>
  a.status === 'signed-in'
    ? { status: 'signed-in', caller: a.caller }
    : a.status === 'refused'
      ? { status: 'signed-out', refused: a.message }
      : a.status === 'unreachable'
        ? { status: 'signed-out', error: a.message }
        : { status: 'signed-out' };

export function AppAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<AppAuthState>({ status: 'loading' });
  const refresh = React.useCallback(async () => setState(stateOf(await fetchAppToken())), []);
  React.useEffect(() => {
    void refresh();
  }, [refresh]);
  const value = React.useMemo(() => ({ state, refresh }), [state, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppAuth() {
  const v = React.useContext(Ctx);
  if (!v) throw new Error('useAppAuth needs <AppAuthProvider>');
  return v;
}

const Loading = () => <div className="min-h-screen bg-page-bg" data-testid="app-auth-loading" />;

/** The routes of a signed-in person; anyone else goes to /login (and comes back after). */
export function RequireSignIn() {
  const { state } = useAppAuth();
  const location = useLocation();
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'signed-out') return <Navigate to="/login" replace state={{ from: location }} />;
  return <Outlet />;
}

/** The sign-in page's route: a signed-in person goes where they were headed. */
export function SignedOutOnly() {
  const { state } = useAppAuth();
  const location = useLocation();
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'signed-in') {
    const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
    return <Navigate to={from ? `${from.pathname}${from.search ?? ''}` : '/'} replace />;
  }
  return <Outlet />;
}
